import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import globalStyles from './styles/outline-show-more.global.pcss?inline';
import encapsulatedStyles from './styles/outline-show-more.inner.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import '../outline-cta/outline-cta';
import '../outline-icon/outline-icon';

/**
 * The OutlineShowMore component
 * A wrapping component that truncates content based on height and provides a "Show More" button
 * @element outline-show-more
 */
@customElement('outline-show-more')
export class OutlineShowMore extends LitElement {
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    encapsulatedCSS: encapsulatedStyles,
  });

  @property({ type: String, attribute: 'truncation-height' })
  truncationHeight = '200px';

  @state()
  private _isExpanded = false;

  @state()
  private _shouldShowButton = false;

  private _contentId = `outline-show-more-content-${Math.random().toString(36).substr(2, 9)}`;

  @query('.content')
  private _contentElement!: HTMLElement;

  @query('.content-wrapper')
  private _contentWrapper!: HTMLElement;

  @query('.sr-announcer')
  private _srAnnouncer!: HTMLElement;

  private _resizeObserver?: ResizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this._setupResizeObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._resizeObserver?.disconnect();
  }

  firstUpdated() {
    this._checkContentHeight();
  }

  private _setupResizeObserver() {
    this._resizeObserver = new ResizeObserver(() => {
      this._checkContentHeight();
    });
  }

  private _checkContentHeight() {
    if (!this._contentElement || !this._contentWrapper) return;

    // Temporarily remove max-height to get natural height
    const originalMaxHeight = this._contentWrapper.style.maxHeight;
    this._contentWrapper.style.maxHeight = 'none';

    const contentHeight = this._contentElement.scrollHeight;
    const truncationHeightPx = this._parseTruncationHeight();

    // Restore original max-height
    this._contentWrapper.style.maxHeight = originalMaxHeight;

    this._shouldShowButton = contentHeight > truncationHeightPx;

    // If content is shorter than truncation height, consider it expanded
    if (!this._shouldShowButton) {
      this._isExpanded = true;
    }

    if (this._resizeObserver && this._contentElement) {
      this._resizeObserver.observe(this._contentElement);
    }
  }

  private _parseTruncationHeight(): number {
    // Convert truncation height to pixels
    if (this.truncationHeight.endsWith('px')) {
      return parseInt(this.truncationHeight, 10);
    }
    if (this.truncationHeight.endsWith('rem')) {
      const remValue = parseFloat(this.truncationHeight);
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
      return remValue * rootFontSize;
    }
    if (this.truncationHeight.endsWith('em')) {
      const emValue = parseFloat(this.truncationHeight);
      const fontSize = parseFloat(getComputedStyle(this).fontSize);
      return emValue * fontSize;
    }
    // Default to pixels if no unit specified
    return parseInt(this.truncationHeight, 10) || 200;
  }

  private _toggleExpanded() {
    this._isExpanded = !this._isExpanded;

    // Announce the state change to screen readers using the persistent aria-live region
    const announcement = this._isExpanded ? 'Content expanded' : 'Content collapsed';
    this._announceToScreenReader(announcement);
  }

  private _announceToScreenReader(message: string) {
    if (this._srAnnouncer) {
      // Clear the announcer first to ensure the message is read
      this._srAnnouncer.textContent = '';

      // Use requestAnimationFrame to ensure the clearing happens before setting new content
      requestAnimationFrame(() => {
        this._srAnnouncer.textContent = message;
      });
    }
  }

  render(): TemplateResult {
    const contentWrapperClasses = [
      'content-wrapper',
      this._isExpanded ? 'expanded' : 'collapsed'
    ].join(' ');

    return html`
      <div class="outline-show-more">
        <!-- Screen reader announcements -->
        <div
          class="sr-announcer"
          aria-live="polite"
          aria-atomic="true"
          style="position: absolute; left: -10000px; width: 1px; height: 1px; overflow: hidden;"
        ></div>

        <div
          class="${contentWrapperClasses}"
          id="${this._contentId}"
          style="${this._isExpanded || !this._shouldShowButton ? '' : `max-height: ${this.truncationHeight}`}"
        >
          <div class="content">
            <slot></slot>
          </div>
        </div>

        ${this._shouldShowButton ? html`
          <div class="button-container print-hide">
            <outline-cta variant="link" size="small">
              <button
                type="button"
                @click="${this._toggleExpanded}"
                aria-expanded="${this._isExpanded}"
                aria-controls="${this._contentId}"
              >
                <outline-icon aria-hidden="true" size="small" icon-classes="fa-solid ${this._isExpanded ? 'fa-minus-circle' : 'fa-plus-circle'}"></outline-icon>
                ${this._isExpanded ? 'Show Less' : 'Show More'}
              </button>
            </outline-cta>
          </div>
        ` : null}
      </div>
    `;
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    'outline-show-more': OutlineShowMore;
  }
}